//
// SearchBarSuggestQns
//
// Renders a single QNS lookup result inside the search dropdown. Mimics the
// visual structure of SearchBarSuggestDomain (the existing BENS row) so it
// looks native to Blockscout's UI.
//
// Layout:
//   Desktop (grid): [ name | address | tier badge ]
//   Mobile  (flex): name on row 1, address on row 2, badge inline
//
// The whole row is wrapped in a NextLink by the parent — clicking navigates
// to `/address/<owner>`.
//
import { Badge, Flex, Grid, Text } from '@chakra-ui/react';
import React from 'react';

import HashStringShortenDynamic from 'ui/shared/HashStringShortenDynamic';

export interface SearchBarSuggestQnsData {
  domain: string;
  owner: string;
  domainType: string | null;
}

interface Props {
  data: SearchBarSuggestQnsData;
  isMobile: boolean | undefined;
}

const SearchBarSuggestQns = ({ data, isMobile }: Props) => {
  const name = (
    <Text
      fontWeight={ 700 }
      overflow="hidden"
      whiteSpace="nowrap"
      textOverflow="ellipsis"
    >
      { data.domain }
    </Text>
  );

  const address = (
    <Text
      overflow="hidden"
      whiteSpace="nowrap"
      variant="secondary"
    >
      <HashStringShortenDynamic hash={ data.owner } isTooltipDisabled/>
    </Text>
  );

  const badge = data.domainType ? (
    <Badge
      colorScheme={ data.domainType === 'paid' ? 'green' : 'gray' }
      textTransform="capitalize"
      fontSize="xs"
    >
      { data.domainType }
    </Badge>
  ) : null;

  if (isMobile) {
    return (
      <>
        <Flex alignItems="center" overflow="hidden" gap={ 2 }>
          { name }
          { badge }
        </Flex>
        <Flex alignItems="center" overflow="hidden">
          { address }
        </Flex>
      </>
    );
  }

  return (
    <Grid alignItems="center" gridTemplateColumns="228px minmax(auto, max-content) auto" gap={ 2 }>
      <Flex alignItems="center" gap={ 2 } overflow="hidden">
        { name }
      </Flex>
      <Flex alignItems="center" overflow="hidden">
        { address }
      </Flex>
      <Flex justifyContent="flex-end">
        { badge }
      </Flex>
    </Grid>
  );
};

export default React.memo(SearchBarSuggestQns);